import React from 'react'
import { Link } from 'react-router-dom'
import { CONTACT_DETAILS } from '../../config/constants.js'
import { useLandingContent } from '../../hooks/i18n/usePageContent.js'
import * as TOKENS from '../chat/design-tokens'

interface LandingFooterProps {
  scrollToContact: () => void
  onAdminAccess?: () => void
}

const isAdminEnabled = typeof window !== 'undefined' && new URLSearchParams(window.location.search).has('admin')
const linkClass = `transition-colors duration-150 ${TOKENS.BRAND.accentHover} ${TOKENS.INTERACTION.focusRing}`

export const LandingFooter: React.FC<LandingFooterProps> = ({
  scrollToContact,
  onAdminAccess,
}) => {
  const { footer, aria, contact } = useLandingContent()

  const contactItems = CONTACT_DETAILS.map(d => ({
    label: d.label === 'Location' ? contact.location : d.value,
    href: d.href,
  }))

  return (
    <footer
      className={`w-full border-t px-6 pb-12 pt-20 md:px-12 ${TOKENS.BORDER.default} ${TOKENS.SURFACE_ROLES.base}`}
      aria-label={aria.siteFooter}
    >
      <div className="mx-auto max-w-6xl">
        <div className="mb-16 grid grid-cols-1 gap-12 md:grid-cols-5 lg:grid-cols-5">
          <div className="flex flex-col gap-5 md:col-span-2 lg:col-span-2">
            <div
              className={`
                flex h-10 w-10 items-center justify-center
                bg-foreground text-background
                font-mono text-xs font-bold select-none leading-tight
              `}
              aria-label={footer.brandAriaLabel}
            >
              F.B<span className={TOKENS.BRAND.slash}>/c</span>
            </div>
            <p className={`max-w-xs text-sm font-light leading-relaxed ${TOKENS.TEXT.secondary}`}>
              {footer.brandBlurb}
            </p>
          </div>

          <nav aria-label={aria.footerServices}>
            <h4
              className={`mb-5 ${TOKENS.TEXT_SIZE.xs} font-bold uppercase tracking-widest ${TOKENS.TEXT.primary}`}
            >
              {footer.columnHeadings.services}
            </h4>
            <ul className={`flex flex-col ${TOKENS.GAP.md} text-sm ${TOKENS.TEXT.secondary}`}>
              {footer.navServices.map(item => (
                <li key={item.label}>
                  <Link to={item.href} className={linkClass}>
                    {item.label}
                  </Link>
                </li>
              ))}
            </ul>
          </nav>

          <div>
            <h4
              className={`mb-5 ${TOKENS.TEXT_SIZE.xs} font-bold uppercase tracking-widest ${TOKENS.TEXT.primary}`}
            >
              {footer.columnHeadings.getInTouch}
            </h4>
            <ul className={`flex flex-col ${TOKENS.GAP.md} text-sm ${TOKENS.TEXT.secondary}`}>
              {contactItems.map(item => (
                <li key={item.label}>
                  {item.href
                    ? <a href={item.href} className={linkClass}>{item.label}</a>
                    : <span>{item.label}</span>
                  }
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4
              className={`mb-5 ${TOKENS.TEXT_SIZE.xs} font-bold uppercase tracking-widest ${TOKENS.TEXT.primary}`}
            >
              {footer.columnHeadings.connect}
            </h4>
            <div className={`flex gap-4 ${TOKENS.TEXT.muted}`}>
              {footer.socialLinks.map(link => (
                <a
                  key={link.label}
                  href={link.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={linkClass}
                  aria-label={link.label}
                >
                  {link.label === 'LinkedIn' ? (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                    </svg>
                  )}
                </a>
              ))}
            </div>
          </div>
        </div>

        <div
          className={`
            flex flex-col items-center justify-between gap-6
            border-t pt-8 md:flex-row
            ${TOKENS.BORDER.default}
          `}
        >
          <span className={`text-xs font-light ${TOKENS.TEXT.muted}`}>
            © {new Date().getFullYear()}{' '}
            <span className="font-mono">
              F.B<span className={TOKENS.BRAND.slash}>/c</span>
            </span>{' '}
            {footer.copyrightSuffix}
          </span>

          <nav
            className={`flex flex-wrap items-center gap-6 text-xs ${TOKENS.TEXT.muted}`}
            aria-label={aria.legalLinks}
          >
            {footer.legalLinks.map(l => (
              <Link key={l.label} to={l.href} className={linkClass}>
                {l.label}
              </Link>
            ))}
            <button onClick={scrollToContact} className={linkClass}>
              {footer.contactButton}
            </button>
            {onAdminAccess && isAdminEnabled && (
              <button
                onClick={onAdminAccess}
                className={`
                  font-mono ${TOKENS.TEXT_SIZE.xs} uppercase tracking-widest
                  opacity-15 transition-opacity duration-200 hover:opacity-60
                  ${TOKENS.INTERACTION.focusRing}
                `}
                title={footer.admin.title}
              >
                {footer.admin.label}
              </button>
            )}
          </nav>
        </div>
      </div>
    </footer>
  )
}
